import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	getAuth,
	EmailAuthProvider,
	reauthenticateWithCredential,
	updatePassword,
} from "firebase/auth";
import { showErrorToast, showSuccessToast } from "../../toastmsg";

const ChangePassword = () => {
	const auth = getAuth();
	const user = auth.currentUser;
	const navigate = useNavigate();

	const [oldPassword, setOldPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmNewPassword, setConfirmNewPassword] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const validatePassword = (password: string) => {
		const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
		return regex.test(password);
	};

	const handleChangePassword = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setSuccess("");

		if (!user || !user.email) {
			setError("No authenticated user found.");
			return;
		}

		if (!validatePassword(newPassword)) {
			setError(
				"Password must be at least 8 characters and include uppercase, lowercase, number, and symbol."
			);
			return;
		}

		if (newPassword !== confirmNewPassword) {
			setError("New passwords do not match.");
			return;
		}

		const credential = EmailAuthProvider.credential(user.email, oldPassword);

		try {
			await reauthenticateWithCredential(user, credential);
			await updatePassword(user, newPassword);
			showSuccessToast("Password updated successfully!");
			setOldPassword("");
			setNewPassword("");
			setConfirmNewPassword("");
			navigate("/dashboard");
		} catch (err: any) {
			showErrorToast(err.message || "Error changing password.");
		}
	};

	return (
		<div>
			<h2 className="text-lg font-bold">Change Password</h2>
			<form
				onSubmit={handleChangePassword}
				className="flex flex-col gap-8 py-10"
			>
				<div className="flex items-center gap-8">
					<label className="w-40">Current Password</label>
					<input
						type="password"
						className="w-60 p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100"
						value={oldPassword}
						onChange={(e) => setOldPassword(e.target.value)}
						required
					/>
				</div>

				<div className="flex flex-col gap-1">
					<div className="flex items-center gap-8">
						<label className="w-40">New Password</label>
						<input
							type="password"
							className="w-60 p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							required
						/>
					</div>
					<p className="text-xs text-gray-600 max-w-md">
						Password must be at least 8 characters and include 1 uppercase, 1
						lowercase, 1 number, and 1 symbol.
					</p>
				</div>

				<div className="flex items-center gap-8">
					<label className="w-40">Confirm New Password</label>
					<input
						type="password"
						className="w-60 p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100"
						value={confirmNewPassword}
						onChange={(e) => setConfirmNewPassword(e.target.value)}
						required
					/>
				</div>
				{error && <div className="text-red-500 text-sm">{error}</div>}

				<div className="flex gap-4">
					<button
						type="submit"
						className="w-52 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
					>
						Change Password
					</button>

					<button
						onClick={() => navigate("/dashboard")}
						className="w-52 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400"
					>
						Cancel
					</button>
				</div>
			</form>
		</div>
	);
};

export default ChangePassword;
